import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeDesigns, atomBadgeSizes} from '@s-ui/react-atom-badge'
import {atomButtonSizes} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import AtomSpinner from '@s-ui/react-atom-spinner'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveLinkBox, {PrimitiveLinkBoxLink, PrimitiveLinkBoxRaised} from '@s-ui/react-primitive-link-box'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontSize,
  primitiveTypographyFontWeight
} from '@s-ui/react-primitive-typography'
import ActionAdFavorite, {actionAdFavoriteTypes} from '@adv-mt/mt-actions-ad-favorite'
import IconArrowDownMedium from '@adv-mt/mt-svg-icons/lib/ArrowDownMedium'

export const CARD_DESIGN = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal'
}

const BASE_CLASS = 'mt-NetAdCard'
const DEFAULT_PHOTO = 'images/no-photo-ad-card.svg'
const NO_OP = () => {}

const NetAdCard = ({
  ad,
  onAdClick = NO_OP,
  onFavoriteChange = NO_OP,
  setIsVisibleLoginModal,
  design = CARD_DESIGN.VERTICAL,
  isWide,
  extendedTrackingData = {}
}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const {URL_VEHICLE_CARD_FALLBACK} = config.get('search')
  const {DRAWER_REOPEN_SESSION_KEY} = config.get('COMMON').AI_ASSISTANT
  const {
    HIT_ORIGINS: {NET_ASSISTANT_AD_FAVORITED},
    AUTHENTICATION_HIT_INFORMATIONS_2
  } = config.get('tracking') || {}

  const handleAdClick = event => {
    onAdClick?.(ad.id, event)
    if (event.defaultPrevented) return
    sessionStorage.setItem(DRAWER_REOPEN_SESSION_KEY, 'true')
  }
  const defaultPhotoSrc = `${urlStatics}${DEFAULT_PHOTO}`

  const firstImage = ad.resources?.find(r => r.type === 'IMAGE')?.url
  const imgSrc = firstImage || ad.images?.[0] || defaultPhotoSrc

  const {title, url, price, km, year, offerType, priceDrop} = ad
  const href = url || `${URL_VEHICLE_CARD_FALLBACK}/${ad.id}/`

  const hasPrice = Boolean(price?.amount)
  const hasKm = Boolean(km)
  const hasYear = Boolean(year)
  const hasOfferType = Boolean(offerType?.literal)

  const isHorizontal = design === CARD_DESIGN.HORIZONTAL
  const getClassByDesign = classname =>
    cx(classname, {
      [`${classname}--horizontal`]: isHorizontal,
      [`${classname}--horizontal--wide`]: isHorizontal && isWide
    })

  const FavoriteButton = (
    <PrimitiveLinkBoxRaised>
      <ActionAdFavorite
        ad={ad}
        isFavorite={ad.isFavorite}
        type={actionAdFavoriteTypes.ICON_BUTTON}
        size={atomButtonSizes.MEDIUM}
        className={getClassByDesign(`${BASE_CLASS}-favoriteButton`)}
        trackingOrigin={NET_ASSISTANT_AD_FAVORITED}
        authenticationHitInformation2={AUTHENTICATION_HIT_INFORMATIONS_2.NET}
        extendedTrackingData={extendedTrackingData}
        onClick={onFavoriteChange}
        setLoginModalVisible={setIsVisibleLoginModal}
      />
    </PrimitiveLinkBoxRaised>
  )

  return (
    <PrimitiveLinkBox className={getClassByDesign(BASE_CLASS)}>
      <div className={getClassByDesign(`${BASE_CLASS}-card`)}>
        <div className={getClassByDesign(`${BASE_CLASS}-media`)}>
          <AtomImage alt={title} spinner={<AtomSpinner />} src={imgSrc} />
          {!isHorizontal && FavoriteButton}
        </div>
        {isHorizontal && FavoriteButton}
        <div className={getClassByDesign(`${BASE_CLASS}-content`)}>
          {hasOfferType && (
            <AtomBadge
              design={atomBadgeDesigns.SOFT}
              label={offerType.literal}
              size={atomBadgeSizes.SMALL}
              type="gray"
            />
          )}
          {hasPrice && (
            <PrimitiveTypography
              as="p"
              className={getClassByDesign(`${BASE_CLASS}-price`)}
              design={primitiveTypographyDesign.BODY_1}
              fontSize={primitiveTypographyFontSize.L}
              fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
            >
              {i18n.c(price.amount)}
            </PrimitiveTypography>
          )}
          {priceDrop && (
            <div className={getClassByDesign(`${BASE_CLASS}-priceDrop`)}>
              <span className={getClassByDesign(`${BASE_CLASS}-priceDropIcon`)}>
                <IconArrowDownMedium size="small" />
              </span>
              <span className={getClassByDesign(`${BASE_CLASS}-priceDropPercentage`)}>
                {priceDrop.percentage * -1}%
              </span>
              <span className={getClassByDesign(`${BASE_CLASS}-priceDropOriginalPrice`)}>
                {i18n.c(priceDrop.originalPrice)}
              </span>
            </div>
          )}
          <PrimitiveLinkBoxLink>
            <a className={getClassByDesign(`${BASE_CLASS}-title`)} href={href} onClick={handleAdClick}>
              {title}
            </a>
          </PrimitiveLinkBoxLink>
          {(hasKm || hasYear) && (
            <PrimitiveTypography
              as="p"
              className={getClassByDesign(`${BASE_CLASS}-info`)}
              design={primitiveTypographyDesign.CAPTION}
            >
              {hasKm && <span>{km.toLocaleString()} km</span>}
              {hasKm && hasYear && <span> | </span>}
              {hasYear && <span>{year}</span>}
            </PrimitiveTypography>
          )}
        </div>
      </div>
    </PrimitiveLinkBox>
  )
}

NetAdCard.displayName = 'NetAdCard'

NetAdCard.propTypes = {
  ad: PropTypes.object.isRequired,
  onAdClick: PropTypes.func,
  onFavoriteChange: PropTypes.func,
  setIsVisibleLoginModal: PropTypes.func,
  design: PropTypes.oneOf(Object.values(CARD_DESIGN)),
  isWide: PropTypes.bool,
  extendedTrackingData: PropTypes.object
}

export default NetAdCard
